/**
 * v2 (26/10/2026) — "+ Add a country": the same copy pasted into several countries in one go
 * (user: "you can paste into several countries at the same time but you have to specify the
 * campaign group of course"). Offers every country with an Automation plan not targeted yet;
 * 27/10/2026: a quiet link-like trigger at the end of the tabs.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo } from 'react';
import { Plus } from 'lucide-react';

import { SearchableSelect } from '@/components/ui';
import { usePlans } from '@/features/drop-generator/useDropGenerator';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';

interface Props {
  /** Countries already targeted. */
  taken: string[];
  nameOf: (countryKey: string) => string;
  onAdd: (countryKey: string) => void;
}

export const AddCountry: FC<Props> = ({ taken, nameOf, onAdd }) => {
  const plans = usePlans();
  const options = useMemo(
    () =>
      (plans.data ?? [])
        .filter((p) => !taken.includes(p.countryKey))
        .map((p) => ({ value: p.countryKey, label: `${countryToFlagEmoji(nameOf(p.countryKey))} ${nameOf(p.countryKey)}` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [plans.data, taken, nameOf],
  );
  if (options.length === 0) return null;
  return (
    <div className="w-36 shrink-0">
      <SearchableSelect
        options={options}
        value=""
        onChange={onAdd}
        placeholder="+ Add country"
        searchPlaceholder="Search a country…"
        className="h-8 border-transparent bg-transparent text-[13px] font-medium !text-primary shadow-none hover:bg-primary/5 [&>span]:flex [&>span]:items-center [&>svg]:hidden"
        headerSlot={<span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Plus className="h-3 w-3" />Paste into this country too</span>}
      />
    </div>
  );
};
